import {
  getFluxmedDefaultTemplateCategory,
  isFluxmedDefaultMessageTemplate,
  resolveFluxmedMetaDefaultTemplateSlugKey,
  resolveFluxmedPlatformDefaultTemplateKey,
  type FluxmedDefaultTemplateCategory,
  type FluxmedMetaDefaultTemplateSlug,
  type FluxmedPlatformDefaultTemplateKey,
} from "./messageTemplateUiUtils";

const FLUXMED_META_DEFAULT_TEMPLATE_USAGE_BY_SLUG: Record<
  FluxmedMetaDefaultTemplateSlug,
  { categoryLabel: FluxmedDefaultTemplateCategory; shortDescription: string }
> = {
  agendamento_de_consulta: {
    categoryLabel: "agendamento",
    shortDescription: "Usado quando uma consulta é agendada.",
  },
  cancelamento_de_consulta: {
    categoryLabel: "cancelamento",
    shortDescription: "Usado quando uma consulta é cancelada.",
  },
  cobranca_de_consulta: {
    categoryLabel: "pagamento",
    shortDescription: "Usado para cobrar o valor de uma consulta.",
  },
  flow_lembrete: {
    categoryLabel: "agendamento",
    shortDescription:
      "Usado para lembrar o paciente de uma consulta ou compromisso.",
  },
  flow_medication: {
    categoryLabel: "outros",
    shortDescription: "Usado em fluxos automáticos relacionados a medicação.",
  },
  flow_exame: {
    categoryLabel: "outros",
    shortDescription: "Usado em fluxos automáticos relacionados a exames.",
  },
};

const FLUXMED_PLATFORM_DEFAULT_TEMPLATE_USAGE_BY_KEY: Record<
  FluxmedPlatformDefaultTemplateKey,
  { categoryLabel: FluxmedDefaultTemplateCategory; shortDescription: string }
> = {
  fluxmedagendamentoteleconsulta: {
    categoryLabel: "agendamento",
    shortDescription: "Usado no agendamento de teleconsultas pela plataforma.",
  },
  fluxmedcancelamentoteleconsulta: {
    categoryLabel: "cancelamento",
    shortDescription: "Usado no cancelamento de teleconsultas pela plataforma.",
  },
  fluxmed_agendamento_consulta: {
    categoryLabel: "agendamento",
    shortDescription: "Usado quando uma consulta é agendada pela plataforma.",
  },
  fluxmed_cancelamento_consulta: {
    categoryLabel: "cancelamento",
    shortDescription: "Usado quando uma consulta é cancelada pela plataforma.",
  },
  teste_flow_medication: {
    categoryLabel: "outros",
    shortDescription: "Usado em fluxos automáticos de medicação.",
  },
  fluxmedpagamentoconsulta: {
    categoryLabel: "pagamento",
    shortDescription: "Usado na cobrança de consultas pela plataforma.",
  },
  flow_service_request_status: {
    categoryLabel: "outros",
    shortDescription: "Usado para informar o status de solicitações de serviço.",
  },
  fluxmed_crm_v1: {
    categoryLabel: "outros",
    shortDescription: "Usado em comunicações automáticas do CRM clínico.",
  },
};

const CATEGORY_SHORT_FALLBACK: Record<FluxmedDefaultTemplateCategory, string> = {
  agendamento: "Usado em confirmações, lembretes ou alterações de consulta.",
  cancelamento: "Usado quando uma consulta é cancelada.",
  pagamento: "Usado quando o paciente necessita pagar um débito.",
  outros: "Usado automaticamente em fluxos específicos da clínica.",
};

function formatShortUsageMessage(
  categoryLabel: FluxmedDefaultTemplateCategory,
  shortDescription: string,
): string {
  return `${categoryLabel}: ${shortDescription}`;
}

export function parseUsageShortMessage(message: string): {
  categoryLabel: string;
  description: string;
} {
  const colonIndex = message.indexOf(":");
  if (colonIndex <= 0) {
    return { categoryLabel: "", description: message.trim() };
  }

  return {
    categoryLabel: message.slice(0, colonIndex).trim().toLowerCase(),
    description: message.slice(colonIndex + 1).trim(),
  };
}

export function getFluxmedDefaultTemplateUsageShortMessage(
  templateName: string,
  organizationPrefix?: string,
  hasActiveMetaEndpoint?: boolean,
): string | null {
  if (
    !isFluxmedDefaultMessageTemplate(templateName, organizationPrefix, {
      hasActiveMetaEndpoint,
    })
  ) {
    return null;
  }

  if (hasActiveMetaEndpoint === true || hasActiveMetaEndpoint === undefined) {
    const metaSlug = resolveFluxmedMetaDefaultTemplateSlugKey(
      templateName,
      organizationPrefix,
    );
    if (metaSlug) {
      const usage = FLUXMED_META_DEFAULT_TEMPLATE_USAGE_BY_SLUG[metaSlug];
      return formatShortUsageMessage(
        usage.categoryLabel,
        usage.shortDescription,
      );
    }
  }

  if (hasActiveMetaEndpoint === false || hasActiveMetaEndpoint === undefined) {
    const platformKey = resolveFluxmedPlatformDefaultTemplateKey(templateName);
    if (platformKey) {
      const usage =
        FLUXMED_PLATFORM_DEFAULT_TEMPLATE_USAGE_BY_KEY[platformKey];
      return formatShortUsageMessage(
        usage.categoryLabel,
        usage.shortDescription,
      );
    }
  }

  const category = getFluxmedDefaultTemplateCategory(
    templateName,
    organizationPrefix,
    hasActiveMetaEndpoint,
  );
  return formatShortUsageMessage(category, CATEGORY_SHORT_FALLBACK[category]);
}
